import { clsx } from "clsx";
import { motion } from "motion/react";
import { useState } from "react";

import alexImage from "../assets/Testimonials/Alex.jpg";
import annaImage from "../assets/Testimonials/Anna.jpg";
import emilyImage from "../assets/Testimonials/Emily.jpg";
import marcusImage from "../assets/Testimonials/Marcus.jpg";
import priyaImage from "../assets/Testimonials/Priya.jpg";
import sarahImage from "../assets/Testimonials/Sarah.jpg";

import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useIsMobile } from "./ui/use-mobile";
import { useReducedMotion } from "./ui/use-reduced-motion";

const testimonials = [
  {
    quote:
      "VERY excited about this and shared it with our designer! Worked exactly like you said it would. Really impressed right out the gate.",
    name: "Sarah",
    role: "Senior Software Engineer",
    company: "TechStart",
    avatar: sarahImage,
  },
  {
    quote:
      "You simplify ideas. I really like the tool to kickstart the design process. It saves us hours every week.",
    name: "Marcus",
    role: "Product Designer",
    company: "CloudScale",
    avatar: marcusImage,
  },
  {
    quote:
      "My experience with using the product is great. Quite impressed with the outcome and the attention to detail.",
    name: "Emily",
    role: "Lead Product Designer",
    company: "DesignFlow",
    avatar: emilyImage,
  },
  {
    quote:
      "Great functionality. Excellent at design tasks. Very impressed with how it handles complex workflows.",
    name: "Anna",
    role: "Software Engineer",
    company: "SaaS Ventures",
    avatar: annaImage,
  },
  {
    quote:
      "Been using Flowstep for some personal projects exclusively in the workflow for app ideas. It is solid and user focused which I love.",
    name: "Alex",
    role: "Senior Product Designer",
    company: "CreativeLabs",
    avatar: alexImage,
  },
  {
    quote:
      "Stunned with the design that I got with a prompt. Importantly the next page ideas was simply awesome. Great work!",
    name: "Priya",
    role: "Software Engineer",
    company: "BuildFast",
    avatar: priyaImage,
  },
];

// Stacked positions (default state)
const stackedPositions = [
  { x: -180, y: -80, rotate: -8, scale: 0.95 }, // Sarah (index 0)
  { x: 120, y: -40, rotate: 4, scale: 0.95 }, // Marcus (index 1)
  { x: -140, y: 100, rotate: -6, scale: 0.95 }, // Emily (index 2)
  { x: 160, y: 80, rotate: 5, scale: 0.95 }, // David (index 3)
  { x: -80, y: 180, rotate: -4, scale: 0.95 }, // Alex (index 4)
  { x: 40, y: 160, rotate: 3, scale: 0.95 }, // Priya (index 5)
];

// Spread positions (hover state)
const spreadPositions = [
  { x: -400, y: -120, rotate: -5, scale: 1 }, // Sarah (index 0) - Top Left
  { x: -40, y: -100, rotate: 2, scale: 1 }, // Marcus (index 1) - Top Center
  { x: 300, y: -80, rotate: 4, scale: 1 }, // Emily (index 2) - Top Right
  { x: -280, y: 100, rotate: -3, scale: 1 }, // David (index 3) - Bottom Left
  { x: 80, y: 140, rotate: 1, scale: 1 }, // Alex (index 4) - Bottom Center
  { x: 420, y: 130, rotate: -2, scale: 1 }, // Priya (index 5) - Bottom Right
];

// Desktop testimonial card component
function TestimonialCard({
  testimonial,
  index,
  isHovered,
}: {
  testimonial: (typeof testimonials)[0];
  index: number;
  isHovered: boolean;
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const position = isHovered ? spreadPositions[index] : stackedPositions[index];

  return (
    <motion.div
      onHoverStart={() => setIsCardHovered(true)}
      onHoverEnd={() => setIsCardHovered(false)}
      animate={
        prefersReducedMotion
          ? {
              x: position.x,
              y: position.y,
              rotate: isCardHovered ? 0 : position.rotate,
              scale: isCardHovered ? (isHovered ? 1.05 : 0.95) : position.scale,
            }
          : {
              x: position.x,
              y: position.y,
              rotate: isCardHovered ? 0 : position.rotate,
              scale: isCardHovered ? (isHovered ? 1.05 : 0.95) : position.scale,
            }
      }
      transition={{
        duration: prefersReducedMotion ? 0 : 0.6,
        ease: [0.22, 1, 0.36, 1],
        delay: prefersReducedMotion ? 0 : isHovered ? index * 0.05 : 0,
      }}
      className="absolute rounded-2xl bg-white p-8 transition-colors duration-300"
      style={{
        width: "360px",
        left: "50%",
        top: "50%",
        marginLeft: "-180px",
        marginTop: "-50px",
        boxShadow:
          "0 8px 30px -8px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: isCardHovered ? "#0247FE" : "#e5e7eb",
      }}
    >
      {/* Decorative dots - top right */}
      <div className="absolute top-4 right-4 flex gap-1.5 opacity-20">
        <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
        <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      </div>

      {/* Decorative dots - bottom left */}
      <div className="absolute bottom-4 left-4 flex gap-1.5 opacity-20">
        <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      </div>

      {/* Author Section */}
      <div className="mb-4 flex items-center gap-3">
        {/* Avatar */}
        <div className="relative h-12 w-12">
          {!imageLoaded && (
            <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200" />
          )}
          <ImageWithFallback
            src={testimonial.avatar}
            alt={testimonial.name}
            className={clsx(
              "h-12 w-12 rounded-full ring-2 ring-white ring-offset-1 transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0",
            )}
            onLoad={() => setImageLoaded(true)}
          />
        </div>

        {/* Text Container */}
        <div className="flex flex-col">
          <div className="text-gray-900" style={{ fontWeight: "600" }}>
            {testimonial.name}
          </div>
          <div className="text-sm text-gray-500">{testimonial.role}</div>
        </div>
      </div>

      {/* Quote */}
      <p
        className="text-gray-700"
        style={{ fontSize: "0.9375rem", lineHeight: "1.6" }}
      >
        "{testimonial.quote}"
      </p>
    </motion.div>
  );
}

// Mobile testimonial card component
function MobileTestimonialCard({
  testimonial,
}: {
  testimonial: (typeof testimonials)[0];
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const firstName = testimonial.name.split(" ")[0];

  return (
    <div
      className="relative w-full rounded-2xl border border-gray-200 bg-white p-8"
      style={{
        boxShadow:
          "0 8px 30px -8px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)",
      }}
    >
      {/* Decorative dots - top right */}
      <div className="absolute top-4 right-4 flex gap-1.5 opacity-20">
        <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
        <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      </div>

      {/* Decorative dots - bottom left */}
      <div className="absolute bottom-4 left-4 flex gap-1.5 opacity-20">
        <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      </div>

      {/* Author Section */}
      <div className="mb-4 flex items-center gap-3">
        {/* Avatar */}
        <div className="relative h-12 w-12">
          {!imageLoaded && (
            <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200" />
          )}
          <ImageWithFallback
            src={testimonial.avatar}
            alt={testimonial.name}
            className={clsx(
              "h-12 w-12 rounded-full ring-2 ring-white ring-offset-1 transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0",
            )}
            onLoad={() => setImageLoaded(true)}
          />
        </div>

        {/* Text Container */}
        <div className="flex flex-col">
          <div className="text-gray-900" style={{ fontWeight: "600" }}>
            {firstName}
          </div>
          <div className="text-sm text-gray-500">{testimonial.role}</div>
        </div>
      </div>

      {/* Quote */}
      <p
        className="text-gray-700"
        style={{ fontSize: "0.9375rem", lineHeight: "1.6" }}
      >
        "{testimonial.quote}"
      </p>
    </div>
  );
}

export default function Testimonials() {
  const prefersReducedMotion = useReducedMotion();
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile();

  return (
    <section className="relative w-full overflow-hidden px-4 py-16 md:px-8 lg:px-16 lg:py-28">
      <div className="relative mx-auto max-w-[1600px]">
        {/* Header */}
        <div className="pb-16 text-center">
          <motion.h2
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={
              prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }
            }
            className="mb-6 leading-[0.95] tracking-tight text-gray-900 dark:text-white"
            style={{
              fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
              fontWeight: "600",
            }}
          >
            Made by designers
          </motion.h2>
          <motion.p
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { duration: 0.6, delay: 0.1 }
            }
            className="mx-auto max-w-3xl text-gray-600 dark:text-gray-300"
            style={{
              fontSize: "clamp(1.125rem, 2.5vw, 1.5rem)",
              lineHeight: "1.625",
            }}
          >
            Loved by teams who ship fast.
          </motion.p>
        </div>

        {/* Desktop Layout */}
        {!isMobile && (
          <div
            className="flex w-full cursor-pointer justify-center"
            style={{ maxWidth: "1200px", height: "500px", margin: "0 auto" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="relative" style={{ width: "100%", height: "100%" }}>
              {testimonials.map((testimonial, index) => (
                <TestimonialCard
                  key={index}
                  testimonial={testimonial}
                  index={index}
                  isHovered={isHovered}
                />
              ))}
            </div>
          </div>
        )}

        {/* Mobile Layout */}
        {isMobile && (
          <div className="mx-auto flex max-w-md flex-col gap-6">
            {testimonials.map((testimonial) => (
              <MobileTestimonialCard
                key={testimonial.name}
                testimonial={testimonial}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
